import { FrameSelectorFieldMui } from '@app/components/form/FrameSelectorFieldMui';
import { SocialPublishAtField } from '@app/modules/social-export/components/inputs/SocialPublishAtField';
import { TitleAndContentFieldsMui } from '@app/modules/social-export/components/inputs/TitleAndContentFieldsMui';
import { BaseExportFormProps } from '@app/modules/social-export/hooks/social-export-form-props';
import { FormSection } from '@app/core/components/FormSection';
import { zodResolver } from '@hookform/resolvers/zod';
import { socialExport } from '@mi-front/api-clients';
import { Box, Grid } from '@mui/material';
import { FormContainer, SwitchElement, TextFieldElement } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { DailymotionCategoryField } from './DailymotionCategoryField';
import { DailymotionPublishValues, useDailymotionPublishSchema } from './useDailymotionPublishSchema';

type DailymotionPublishFormProps = BaseExportFormProps<
  socialExport.DailymotionPublishDto,
  socialExport.DailymotionSettingsDto
>;

export function DailymotionPublishForm({
  source,
  onUpload,
  defaultSettings,
  actions,
}: DailymotionPublishFormProps) {
  const { $t } = useIntl();
  const schema = useDailymotionPublishSchema();
  const initialValues: DailymotionPublishValues = {
    title: source.title ?? '',
    content: source.description ?? '',
    tags: source.tags.join(', '),
    forKids: defaultSettings?.forKids ?? false,
    private: defaultSettings?.private ?? true,
    channel: defaultSettings?.channel ?? null,
    thumbOffsetMs: null,
    publishAt: null,
  };

  const beforeSubmit = (data: DailymotionPublishValues): socialExport.DailymotionPublishDto => ({
    title: data.title,
    description: data.content,
    tags: data.tags
      ? data.tags
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
    forKids: data.forKids,
    private: data.private,
    thumbOffsetMs: data.thumbOffsetMs ?? undefined,
    publishAt: data.publishAt || undefined,
    channel: (data.channel as socialExport.DailymotionPublishDtoChannelEnumKey) || undefined,
  });

  return (
    <FormContainer
      values={initialValues}
      mode="onBlur"
      resolver={zodResolver(schema)}
      onSuccess={async (formData) => {
        await onUpload(beforeSubmit(formData));
      }}
    >
      <FormSection
        title={$t({
          id: 'socialExport.form.content',
          defaultMessage: 'Contenido',
        })}
      >
        <TitleAndContentFieldsMui />
        <Grid container>
          <Grid item md={8}>
            <TextFieldElement
              name="tags"
              label={$t({
                id: 'socialExport.form.tags',
                defaultMessage: 'Tags',
              })}
              helperText={$t({
                id: 'socialExport.form.commaSeparatedValues',
                defaultMessage: 'Valores separados por coma',
              })}
              fullWidth
            />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection
        title={$t({
          id: 'socialExport.form.settings',
          defaultMessage: 'Configuración',
        })}
      >
        <Grid container>
          <Grid item md={8}>
            <DailymotionCategoryField />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={6}>
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={2}>
              <SwitchElement
                name="forKids"
                label={$t({
                  id: 'socialExport.form.forKids',
                  defaultMessage: 'Contenido apto para niños',
                })}
                color="primary"
              />
              <SwitchElement
                name="private"
                label={$t({
                  id: 'socialExport.form.uploadAsPrivate',
                  defaultMessage: 'Cargar como video privado',
                })}
                color="primary"
              />
            </Box>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={6}>
            <FrameSelectorFieldMui name="thumbOffsetMs" unit="ms" />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection
        title={$t({
          id: 'socialExport.form.schedule',
          defaultMessage: 'Programación',
        })}
      >
        <Grid container>
          <Grid item md={12}>
            <SocialPublishAtField />
          </Grid>
        </Grid>
      </FormSection>
      {actions}
    </FormContainer>
  );
}
